import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CourseItem } from '../../core/state/course/models';
import { FormatterService } from '../../util/formatter.service';
import { Router } from '@angular/router';

@Component({
  selector: 'black-course-card',
  templateUrl: './course-card.component.html',
  styleUrls: ['./course-card.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class CourseCardComponent {
  private formatter = inject(FormatterService);
  private router = inject(Router);

  @Input() course!: CourseItem;
  @Input() isEnrolled = false;
  @Input() enrollmentStatus?:
    | 'ACTIVE'
    | 'AWAITING_APPROVAL'
    | 'PENDING'
    | 'OVERDUE'
    | string;
  @Input() orderId?: string;

  @Output() cardClicked = new EventEmitter<string>();
  @Output() enrollClicked = new EventEmitter<string>();

  isImageError = false;

  get isOverdue(): boolean {
    return this.enrollmentStatus === 'OVERDUE';
  }

  get isPendingApproval(): boolean {
    return this.enrollmentStatus === 'AWAITING_APPROVAL';
  }

  get isPaymentPending(): boolean {
    return this.enrollmentStatus === 'PENDING';
  }

  get courseId(): string {
    return this.course?._id || '';
  }

  get bannerImage(): string {
    return this.formatter.getResourceImagePath(
      this.course?.settings?.bannerImageResource,
    );
  }

  get targetAudience(): string {
    return this.course?.targetAudience || '';
  }

  get subject(): string {
    return this.course?.subject || '';
  }

  get categoryName(): string {
    return this.formatter.getCategoryName(this.course?.category) || 'Course';
  }

  get totalLessons(): number {
    if (!this.course?.contents) return 0;
    return this.course.contents.reduce(
      (sum, content) => sum + (content.resources?.length || 0),
      0,
    );
  }

  get totalModules(): number {
    return this.course?.contents?.length || 0;
  }

  get durationLabel(): string {
    const hours = this.course?.details?.totalDurationHour || 0;
    const minutes = this.course?.details?.totalDurationMinutes || 0;
    if (hours === 0 && minutes === 0) return 'Self-paced';
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} hr`;
    return `${hours} hr ${minutes} min`;
  }

  get price(): number {
    return this.course?.settings?.price ?? 0;
  }

  get priceFormatted(): string {
    if (this.price === 0) return 'Free';
    return `LKR ${this.price.toLocaleString('en-LK')}`;
  }

  get medium(): string {
    return this.course?.details?.medium || 'ENGLISH';
  }

  get mediumFormatted(): string {
    switch (this.medium) {
      case 'TAMIL':
        return 'Tamil Medium';
      case 'SINHALA':
        return 'Sinhala Medium';
      default:
        return 'English Medium';
    }
  }

  getInitials(title?: string): string {
    if (!title) return 'C';
    const words = title.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return title.substring(0, 2).toUpperCase();
  }

  get pendingStatus(): {
    icon: string;
    label: string;
    badgeClass: string;
  } | null {
    if (this.isOverdue)
      return {
        icon: 'fa-triangle-exclamation',
        label: 'Overdue',
        badgeClass: 'daisy-badge-error text-white',
      };
    if (this.isPendingApproval)
      return {
        icon: 'fa-clock',
        label: 'Verification Pending',
        badgeClass: 'daisy-badge-info text-info-content',
      };
    if (this.isPaymentPending)
      return {
        icon: 'fa-hourglass-half',
        label: this.isEnrolled ? 'Installment Due' : 'Payment Pending',
        badgeClass: 'daisy-badge-warning text-warning-content',
      };
    return null;
  }

  onImageError(): void {
    this.isImageError = true;
  }

  private handleNavigate(fallbackEmitter: EventEmitter<string>): void {
    if (this.isOverdue && this.orderId) {
      this.router.navigate(['/order/payment'], {
        queryParams: { orderId: this.orderId },
      });
    } else if (this.isEnrolled) {
      this.router.navigate(['/courses', this.courseId, 'playlist']);
    } else if (this.isPendingApproval) {
      this.router.navigate(['/dashboard/orders']);
    } else if (this.isPaymentPending && this.orderId) {
      this.router.navigate(['/order/payment'], {
        queryParams: { orderId: this.orderId },
      });
    } else {
      fallbackEmitter.emit(this.courseId);
      this.router.navigate(['/courses', this.courseId]);
    }
  }

  onCardClick(): void {
    this.handleNavigate(this.cardClicked);
  }

  onActionClick(event: MouseEvent): void {
    event.stopPropagation();
    this.handleNavigate(this.enrollClicked);
  }
}
